import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Swords, Users } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { Loader } from '@/components/common/Loader';
import { ChessStatusBadge } from '@/components/chess/ChessStatusBadge';
import type { BracketStatus, ChessChallengeFormat } from '@/types/qchess';
import { useListBracketsQuery } from '@/services/qchessApi';
import { classifyQchessError, qchessErrorKey } from '@/services/qchessErrors';
import { cn } from '@/lib/cn';

type FilterValue = 'all' | BracketStatus;
const FILTERS: readonly FilterValue[] = [
  'all',
  'registration',
  'ready',
  'in_progress',
  'complete',
  'cancelled',
];

/** 'bo3' → 3, for the "Best of {{n}}" label. */
function bestOfNum(bestOf: ChessChallengeFormat): number {
  return bestOf === 'bo5' ? 5 : bestOf === 'bo3' ? 3 : 1;
}

/**
 * Knockout brackets — filterable list. Consumer-only: brackets are created and
 * run from the qchess-backoffice (the dedicated admin SPA), not the TMA — the
 * TMA's Logto token doesn't carry the `qchess:admin` scope. Each row links to
 * the bracket detail (tree).
 */
export function ChessBrackets() {
  const { t } = useTranslation('chess');
  const haptics = useHaptics();

  const [filter, setFilter] = useState<FilterValue>('all');

  const listQuery = useListBracketsQuery(filter === 'all' ? undefined : { status: filter });
  const brackets = listQuery.data?.brackets ?? [];

  return (
    <div className="flex flex-col gap-4 px-4 pb-6 pt-5">
      <header className="flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand/15 text-brand">
          <Swords aria-hidden className="h-5 w-5" />
        </span>
        <h1 className="text-2xl font-bold text-fg">{t('bracket.title')}</h1>
      </header>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => {
              haptics.selection();
              setFilter(f);
            }}
            className={cn(
              'rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide transition-opacity active:opacity-80',
              filter === f ? 'bg-brand text-brand-fg' : 'bg-bg-secondary text-fg-muted',
            )}
          >
            {t(`bracket.filter.${f}`)}
          </button>
        ))}
      </div>

      {listQuery.isLoading ? (
        <Loader />
      ) : listQuery.error ? (
        <p className="rounded-md bg-danger/15 px-3 py-2 text-sm text-danger" role="alert">
          {t(qchessErrorKey(classifyQchessError(listQuery.error)))}
        </p>
      ) : brackets.length === 0 ? (
        <p className="py-8 text-center text-sm text-fg-muted">{t('bracket.empty')}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {brackets.map((b) => {
            const clock = `${Math.round(b.clockLimitSeconds / 60)}+${b.clockIncrementSeconds}`;
            return (
              <li key={b._id}>
                <Link
                  to={`/chess/brackets/${encodeURIComponent(b._id)}`}
                  onClick={() => haptics.impact('light')}
                  className="flex items-center gap-3 rounded-xl bg-bg-secondary p-3 transition-opacity active:opacity-80"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-semibold text-fg">
                        {b.name || t('bracket.title')}
                      </p>
                      <ChessStatusBadge status={b.status} variant="bracket" />
                    </div>
                    <p className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-fg-muted">
                      <span>{t('bracket.bestOf', { n: bestOfNum(b.bestOf) })}</span>
                      <span aria-hidden>·</span>
                      <span>{clock}</span>
                      <span aria-hidden>·</span>
                      <span className="flex items-center gap-1">
                        <Users aria-hidden className="h-3 w-3" />
                        {t('bracket.players', { count: b.participants.length })}
                      </span>
                    </p>
                  </div>
                  <ChevronRight aria-hidden className="h-5 w-5 shrink-0 text-fg-muted" />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
